import { FC, useState } from 'react';
import styled from '@emotion/styled';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import appConfig from 'app-config';
import IcCheckCircleFill from 'src/assets/images/icCheckCircleFill.svg';
import Button from 'src/components/Button';
import ModalLayoutBase, { ModalContent, ModalFooter, ModalHeader, ModalInner, ModalTitle as ModalTitleBase } from 'src/components/ModalLayout';
import { useGetTermsUrl } from 'src/hooks/useGetTermsUrl';
import { useRestMutation } from 'src/hooks/useRestMutation';
import { ConfirmGuidelineRequest, GuidelineApiList } from 'src/stores/auth/apis';
import { CreatorOnboardingStep, creatorOnboardingStepAtom } from 'src/stores/creator/atom';
import { showCommonErrorToastAtom } from 'src/stores/toast/atoms';
import { ModalNavButtonType } from 'src/types/Modal';
import { setTargetBlankToLinks } from 'src/utils/html';
const ModalLayout = styled(ModalLayoutBase)`
  width: 500px;
  max-height: min(calc(var(--100vh) - 40px), 800px);
  ${ModalInner} {
    max-height: inherit;
  }
`;
const GuidelinesWrap = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 20px;
  min-height: 0;
  overflow: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }
`;
const GuidelineWrap = styled.div`
  border-radius: 12px;
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  border: 1px solid ${({
  theme
}) => theme.color.surface5};
`;
const GuidelineTitle = styled.p`
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  color: ${({
  theme
}) => theme.color.contentDeep};
`;
const GuidelineContent = styled.div``;
const GuidelineTextBase = styled.p`
  font-weight: 400;
  font-size: 12px;
  line-height: 15px;
  color: ${({
  theme
}) => theme.color.content9};

  a {
    all: unset;
    text-decoration: underline;
    cursor: pointer;
  }
`;
const GuidelineSubText = styled(GuidelineTextBase)`
  display: flex;

  &::before {
    content: '•';
    /**
     * 줄넘김 이후에도 bullet point 전후 여백 유지
    */
    flex-shrink: 0;
    margin: 0 7.5px;
  }
`;
const GuidelineText = styled(GuidelineTextBase)`
  & + &,
  & + ${GuidelineSubText} {
    /**
     * Paragraph spacing
     */
    margin-top: 4px;
  }
`;
const CheckWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  background-color: ${({
  theme
}) => theme.color.surface4};
  padding: 18px 16px 18px 20px;
  border-radius: 16px;
  cursor: pointer;
`;
const CheckBox = styled.div<{
  isChecked: boolean;
}>`
  svg {
    width: 24px;
    height: 24px;
    color: ${({
  isChecked,
  theme
}) => isChecked ? theme.color.contentDeep : theme.color.content6};
  }
`;
const CheckBoxContentWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const CheckBoxContentTitle = styled.p`
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  color: ${({
  theme
}) => theme.color.content12};
`;
const CheckBoxContentDescription = styled.p`
  font-size: 12px;
  font-weight: 400;
  line-height: 15px;
  color: ${({
  theme
}) => theme.color.content8};
`;
const ModalTitle = styled(ModalTitleBase)`
  font-size: 20px;
  line-height: 26px;
`;
const CreatorGuidelineModal: FC = () => {
  const {
    mutateAsync,
    isPending: isLoading
  } = useRestMutation<ConfirmGuidelineRequest>({
    path: GuidelineApiList.ConfirmGuideline,
    method: 'post'
  });
  const showCommonErrorToast = useSetAtom(showCommonErrorToastAtom);
  const setCreatorOnboardingStep = useSetAtom(creatorOnboardingStepAtom);
  const {
    t
  } = useTranslation();
  const [isChecked, setIsChecked] = useState(false);
  const {
    termOfUse
  } = useGetTermsUrl();
  const handleClickAgreeButton = async () => {
    try {
      await mutateAsync({
        type: 'CREATOR_GUIDE'
      });
      setCreatorOnboardingStep(CreatorOnboardingStep.Tutorials);
    } catch {
      showCommonErrorToast();
    }
  };
  const handleClickCheckBox = () => {
    setIsChecked(!isChecked);
  };
  return <ModalLayout closeWhenClickOutside={false} navButtonType={ModalNavButtonType.NONE} mobileNavButtonType={ModalNavButtonType.NONE} isDim data-sentry-element="ModalLayout" data-sentry-component="CreatorGuidelineModal" data-sentry-source-file="CreatorGuidelineModal.tsx">
      <ModalHeader data-sentry-element="ModalHeader" data-sentry-source-file="CreatorGuidelineModal.tsx">
        <ModalTitle data-sentry-element="ModalTitle" data-sentry-source-file="CreatorGuidelineModal.tsx">{t('tutorial_guideline_title')}</ModalTitle>
      </ModalHeader>
      <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="CreatorGuidelineModal.tsx">
        <GuidelinesWrap data-sentry-element="GuidelinesWrap" data-sentry-source-file="CreatorGuidelineModal.tsx">
          <GuidelineWrap data-sentry-element="GuidelineWrap" data-sentry-source-file="CreatorGuidelineModal.tsx">
            <GuidelineTitle data-sentry-element="GuidelineTitle" data-sentry-source-file="CreatorGuidelineModal.tsx">{t('tutorial_guideline_azar_title')}</GuidelineTitle>
            <GuidelineContent data-sentry-element="GuidelineContent" data-sentry-source-file="CreatorGuidelineModal.tsx">
              <GuidelineText dangerouslySetInnerHTML={{
              __html: setTargetBlankToLinks(t('tutorial_guideline_desc1', {
                azarCommunityGuideLink: appConfig.helpCenterArticleUrls.communityGuidelines,
                azarTermsOfServiceLink: termOfUse
              }))
            }} data-sentry-element="GuidelineText" data-sentry-source-file="CreatorGuidelineModal.tsx" />
              <GuidelineText data-sentry-element="GuidelineText" data-sentry-source-file="CreatorGuidelineModal.tsx">{t('tutorial_guideline_desc2')}</GuidelineText>
              <GuidelineText data-sentry-element="GuidelineText" data-sentry-source-file="CreatorGuidelineModal.tsx">{t('tutorial_guideline_desc3')}</GuidelineText>
              <GuidelineSubText data-sentry-element="GuidelineSubText" data-sentry-source-file="CreatorGuidelineModal.tsx">{t('tutorial_guideline_desc3_sub1')}</GuidelineSubText>
              <GuidelineSubText data-sentry-element="GuidelineSubText" data-sentry-source-file="CreatorGuidelineModal.tsx">{t('tutorial_guideline_desc3_sub2')}</GuidelineSubText>
              <GuidelineSubText data-sentry-element="GuidelineSubText" data-sentry-source-file="CreatorGuidelineModal.tsx">{t('tutorial_guideline_desc4')}</GuidelineSubText>
              <GuidelineSubText data-sentry-element="GuidelineSubText" data-sentry-source-file="CreatorGuidelineModal.tsx">{t('tutorial_guideline_desc5')}</GuidelineSubText>
            </GuidelineContent>
          </GuidelineWrap>
          <GuidelineWrap data-sentry-element="GuidelineWrap" data-sentry-source-file="CreatorGuidelineModal.tsx">
            <GuidelineTitle data-sentry-element="GuidelineTitle" data-sentry-source-file="CreatorGuidelineModal.tsx">{t('tutorial_guideline_youtube_title')}</GuidelineTitle>
            <GuidelineContent data-sentry-element="GuidelineContent" data-sentry-source-file="CreatorGuidelineModal.tsx">
              <GuidelineText dangerouslySetInnerHTML={{
              __html: setTargetBlankToLinks(t('tutorial_guideline_youtube_desc1', {
                youtubeTermsOfServiceLink: 'https://www.youtube.com/t/terms'
              }))
            }} data-sentry-element="GuidelineText" data-sentry-source-file="CreatorGuidelineModal.tsx" />
              <GuidelineText dangerouslySetInnerHTML={{
              __html: setTargetBlankToLinks(t('tutorial_guideline_youtube_desc2', {
                googlePrivacyPolicyLink: 'https://www.google.com/policies/privacy'
              }))
            }} data-sentry-element="GuidelineText" data-sentry-source-file="CreatorGuidelineModal.tsx" />
              <GuidelineText dangerouslySetInnerHTML={{
              __html: setTargetBlankToLinks(t('tutorial_guideline_youtube_desc3', {
                googleSecuritySettingsLink: 'https://security.google.com/settings/'
              }))
            }} data-sentry-element="GuidelineText" data-sentry-source-file="CreatorGuidelineModal.tsx" />
            </GuidelineContent>
          </GuidelineWrap>
        </GuidelinesWrap>
      </ModalContent>
      <ModalFooter data-sentry-element="ModalFooter" data-sentry-source-file="CreatorGuidelineModal.tsx">
        <CheckWrap onClick={handleClickCheckBox} role='button' tabIndex={0} data-sentry-element="CheckWrap" data-sentry-source-file="CreatorGuidelineModal.tsx">
          <CheckBoxContentWrap data-sentry-element="CheckBoxContentWrap" data-sentry-source-file="CreatorGuidelineModal.tsx">
            <CheckBoxContentTitle data-sentry-element="CheckBoxContentTitle" data-sentry-source-file="CreatorGuidelineModal.tsx">
              {t('tutorial_guideline_confirm_agree_title')}
            </CheckBoxContentTitle>
            <CheckBoxContentDescription data-sentry-element="CheckBoxContentDescription" data-sentry-source-file="CreatorGuidelineModal.tsx">
              {t('tutorial_guideline_confirm_agree_desc')}
            </CheckBoxContentDescription>
          </CheckBoxContentWrap>

          <CheckBox isChecked={isChecked} data-sentry-element="CheckBox" data-sentry-source-file="CreatorGuidelineModal.tsx">
            <IcCheckCircleFill data-sentry-element="IcCheckCircleFill" data-sentry-source-file="CreatorGuidelineModal.tsx" />
          </CheckBox>
        </CheckWrap>
        <Button id='Creator-Guideline-Agree' onClick={handleClickAgreeButton} loading={isLoading} disabled={!isChecked} data-sentry-element="Button" data-sentry-source-file="CreatorGuidelineModal.tsx">
          {t('tutorial_guideline_btn')}
        </Button>
      </ModalFooter>
    </ModalLayout>;
};
export default CreatorGuidelineModal;